import { createSlice } from '@reduxjs/toolkit';
import config from '../app/config';
import i18n from '../app/i18n';
import { NotifyRecipientType, SnackBarType } from '../app/types';
import {
  parseJwt,
  validateMax,
  validateMessages,
  validateText,
} from '../app/utils';
import CouponService from '../services/CouponService';
import ECBindingService from '../services/ECBindingService';
import FileService from '../services/FileService';
import errorHandle from './errorHandle';
import { enqueueSnackbar } from './notifierSlice';

export const ecBindingSlice = createSlice({
  name: 'ecBinding',
  initialState: {
    display: null,
    data: {
      credential: {
        token: '',
      },
      merchant: {
        domain: '',
      },
      syncUserInfo: false,
      liffSetting: {
        liffId: '',
        channelId: '',
        channelSecret: '',
      },
      policy: {
        pageTitle: '會員綁定',
        title: '服務條款及隱私政策',
        content: '',
        btnBgColor: '#b5d100',
      },
      bindPage: {
        pageTitle: '會員綁定',
        logoImageUrl: config.DEFAULT_LOGO_IMAGE_URL,
        btnBgColor: '#b5d100',
        accountPlaceHolder: '請輸入會員電子信箱或手機號碼',
      },
      bindResult: {
        success: {
          messages: [
            {
              index: 0,
              contentType: 'text/plain',
              data: {
                content:
                  ' <%=name%> 您好，您已完成會員綁定！您可以透過此官方帳號直接查詢訂單等狀態資訊 ❤️',
              },
            },
          ],
          tag: '',
          notifyRecipient: NotifyRecipientType.ALL,
          triggerCoupon: {
            enabled: false,
            couponSetId: '',
          },
        },
        fail: {
          messages: [
            {
              index: 0,
              contentType: 'text/plain',
              data: {
                content: '綁定失敗，請確認是否已成為官網的會員！',
              },
            },
          ],
          tag: '',
        },
      },
      actions: {
        orderSearch: {
          keyword: '訂單查詢',
          messages: {
            noOrder: [
              {
                index: 0,
                contentType: 'text/plain',
                data: {
                  content: ' <%=name%> 您好，目前沒有任何訂單資料，謝謝。',
                },
              },
            ],
          },
        },
      },
    },
    isLoading: false,
    isSaved: false,
    isShowOptionalDialog: false,
    isShowConfirmCardDialog: false,
    isShowImageCardDialog: false,
    isSaveAsDefaultTemplate: false,
    isShowCardDialogTemplateIndex: 0,
    defaultTemplate: {
      isShow: false,
      templateType: 'all',
      messageIndex: 0,
    },
    couponList: [],
  },
  reducers: {
    setDisplay: (state, action) => ({
      ...state,
      display: action.payload,
    }),
    setToken: (state, action) => ({
      ...state,
      token: action.payload,
    }),
    setCouponList: (state, action) => ({
      ...state,
      couponList: action.payload,
    }),
    setIsSaved: (state, action) => ({
      ...state,
      isSaved: action.payload,
    }),
    setIsLoading: (state, action) => ({
      ...state,
      isLoading: action.payload,
    }),
    setData: (state, action) => ({
      ...state,
      data: {
        ...state.data,
        ...action.payload,
      },
    }),
    setState: (state, action) => ({
      ...state,
      ...action.payload,
    }),
    setDefaultTemplate: (state, action) => ({
      ...state,
      defaultTemplate: {
        ...state.defaultTemplate,
        ...action.payload,
      },
    }),
  },
});

export const {
  setDisplay,
  setToken,
  setCouponList,
  setDefaultTemplate,
  setIsSaved,
  setIsLoading,
  setData,
  setState,
} = ecBindingSlice.actions;

export const getData = (orgId, provider) => async dispatch => {
  try {
    dispatch(setIsLoading(true));
    dispatch(setIsSaved(false));

    const response = await ECBindingService.getData(orgId, provider);

    dispatch(setData(response));
    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setState({ data: {}, isLoading: false }));
    if (error.response?.data?.code !== 'EcSettingNotFoundError') {
      dispatch(errorHandle(error));
    }
  }
};

const checkLiffSetting = data => {
  const { liffSetting } = data || {};
  const { liffId, channelId, channelSecret } = liffSetting || {};
  return (
    validateText(liffId) &&
    validateText(channelId) &&
    validateText(channelSecret)
  );
};

const checkData = data => {
  const isStep2Verify =
    validateText(data?.policy?.pageTitle) &&
    validateMax(data?.policy?.pageTitle, 20) &&
    validateText(data?.policy?.title) &&
    validateMax(data?.policy?.title, 12) &&
    validateText(data?.policy?.content);
  const isStep3Verify =
    validateText(data?.bindPage?.pageTitle) &&
    validateMax(data?.bindPage?.pageTitle, 20);
  const isStep4Verify = validateMessages(data?.bindResult?.success?.messages);
  const isStep5Verify = validateMessages(data?.bindResult?.fail?.messages);

  return isStep2Verify && isStep3Verify && isStep4Verify && isStep5Verify;
};

export const saveData = (orgId, provider) => async (dispatch, getStore) => {
  try {
    dispatch(setIsLoading(true));
    dispatch(setIsSaved(true));

    const { data } = getStore().ecBinding;

    const isLiffSettingVerify = checkLiffSetting(data);
    const isDataVerify = checkData(data);

    if (isDataVerify && isLiffSettingVerify) {
      await ECBindingService.saveData(orgId, provider, {
        syncUserInfo: data?.syncUserInfo,
        liffSetting: data?.liffSetting,
        policy: data?.policy,
        bindPage: data?.bindPage,
        bindResult: data?.bindResult,
      });
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`success-save`),
        }),
      );
    } else if (!isLiffSettingVerify) {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: i18n.t(`liff-data-error`),
        }),
      );
    } else {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: i18n.t(`data-error`),
        }),
      );
    }

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const connect = (orgId, provider) => async (dispatch, getStore) => {
  try {
    dispatch(setIsLoading(true));
    dispatch(setIsSaved(true));

    const { data } = getStore().ecBinding;

    const isDomainVerify =
      provider === 'shopify'
        ? validateText(data?.merchant?.domain) &&
          validateText(data?.credential?.webhookKey) &&
          validateText(data?.credential?.webhookSecret)
        : true;

    const isTokenVerify =
      provider === 'cyberbiz' ? true : validateText(data?.credential?.token);

    const isVerify = isTokenVerify && isDomainVerify;

    if (isVerify) {
      await ECBindingService.connect(orgId, provider, {
        token: data?.credential?.token?.trim(),
        domainUrl: data?.merchant?.domain?.trim(),
        webhookKey: data?.credential?.webhookKey?.trim(),
        webhookSecret: data?.credential?.webhookSecret?.trim(),
      });

      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.SUCCESS,
          message: i18n.t(`ecBinding:${provider}-connect-success`),
        }),
      );
    } else {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: i18n.t(`data-error`),
        }),
      );
    }

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const disconnect = (orgId, provider) => async dispatch => {
  try {
    dispatch(setIsLoading(true));

    await ECBindingService.disconnect(orgId, provider);

    dispatch(
      enqueueSnackbar({
        variant: SnackBarType.SUCCESS,
        message: i18n.t(`ecBinding:${provider}-disconnect-success`),
      }),
    );

    dispatch(setData({ merchant: undefined, credential: undefined }));
    await dispatch(getData(orgId, provider));

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const saveOrderSearch =
  (orgId, provider) => async (dispatch, getStore) => {
    try {
      dispatch(setIsLoading(true));

      const { data } = getStore().ecBinding;

      const isVerify =
        validateText(data?.actions?.orderSearch?.keyword) &&
        validateMessages(data?.actions?.orderSearch?.messages?.noOrder);

      if (isVerify) {
        await ECBindingService.saveData(orgId, provider, {
          actions: data?.actions,
        });

        await dispatch(getData(orgId, provider));
        dispatch(
          enqueueSnackbar({
            variant: SnackBarType.SUCCESS,
            message: i18n.t(`success-save`),
          }),
        );
      } else {
        dispatch(
          enqueueSnackbar({
            variant: SnackBarType.ERROR,
            message: i18n.t(`data-error`),
          }),
        );
      }

      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(errorHandle(error));
    }
  };

export const getCouponList = orgId => async dispatch => {
  try {
    const list = await CouponService.getList(orgId);

    dispatch(setCouponList(list));
  } catch (error) {
    dispatch(setCouponList([]));
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error));
  }
};

export const uploadFile = file => async dispatch => {
  try {
    const response = await FileService.upload(file);

    if (response) {
      return response?.url;
    }
    return '';
  } catch (error) {
    dispatch(errorHandle(error.response?.data?.error || error.message));

    return '';
  }
};

// dataKey => success, fail
export const stepAddMessage =
  (dataKey = 'success') =>
  async (dispatch, getStore) => {
    const { data = {} } = getStore().ecBinding;
    const { bindResult = {} } = data || {};
    const { messages = [] } = bindResult[dataKey] || {};

    dispatch(
      setData({
        bindResult: {
          ...bindResult,
          [dataKey]: {
            ...bindResult[dataKey],
            messages: [
              ...(messages || []),
              {
                index: (messages || []).length,
                contentType: 'text/plain',
                data: {
                  content: '',
                },
              },
            ],
          },
        },
      }),
    );
  };

// dataKey => success, fail
export const stepDeleteMessage =
  (index, dataKey = 'success') =>
  async (dispatch, getStore) => {
    const { data = {} } = getStore().ecBinding;
    const { bindResult = {} } = data || {};
    const { messages = [] } = bindResult[dataKey] || {};

    dispatch(
      setData({
        bindResult: {
          ...bindResult,
          [dataKey]: {
            ...bindResult[dataKey],
            messages: (messages || [])
              .filter((message, messageIndex) => messageIndex !== index)
              .map((message, messageIndex) => {
                return {
                  ...message,
                  index: messageIndex,
                };
              }),
          },
        },
      }),
    );
  };

// dataKey => success, fail
export const stepUpMessage =
  (index, dataKey = 'success') =>
  async (dispatch, getStore) => {
    const { data = {} } = getStore().ecBinding;
    const { bindResult = {} } = data || {};
    const messages = [...((bindResult[dataKey] || {})?.messages || [])];
    const tempMessage = messages[index - 1];
    messages[index - 1] = messages[index];
    messages[index] = tempMessage;

    dispatch(
      setData({
        bindResult: {
          ...bindResult,
          [dataKey]: {
            ...bindResult[dataKey],
            messages: messages.map((message, messageIndex) => {
              return {
                ...message,
                index: messageIndex,
              };
            }),
          },
        },
      }),
    );
  };

// dataKey => success, fail
export const stepDownMessage =
  (index, dataKey = 'success') =>
  async (dispatch, getStore) => {
    const { data = {} } = getStore().ecBinding;
    const { bindResult = {} } = data || {};
    const messages = [...((bindResult[dataKey] || {})?.messages || [])];
    const tempMessage = messages[index + 1];
    messages[index + 1] = messages[index];
    messages[index] = tempMessage;

    dispatch(
      setData({
        bindResult: {
          ...bindResult,
          [dataKey]: {
            ...bindResult[dataKey],
            messages: messages.map((message, messageIndex) => {
              return {
                ...message,
                index: messageIndex,
              };
            }),
          },
        },
      }),
    );
  };

export const getDisplayData = (orgId, provider, token) => async dispatch => {
  try {
    dispatch(setIsLoading(true));

    const response = await ECBindingService.getDisplayData(
      orgId,
      provider,
      token,
    );
    dispatch(setDisplay(response));

    dispatch(setIsLoading(false));
  } catch (error) {
    dispatch(setDisplay(null));
    dispatch(setIsLoading(false));
    dispatch(errorHandle(error, true));
  }
};

export const sendVerification =
  (orgId, account, customerId, token, provider) => async dispatch => {
    try {
      dispatch(setIsLoading(true));

      await ECBindingService.sendVerification(
        orgId,
        account,
        customerId,
        token,
        provider,
      );

      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(errorHandle(error, true));
    }
  };

export const bind =
  (orgId, customerId, ecCustomerId, authcode, token, provider) =>
  async dispatch => {
    try {
      dispatch(setIsLoading(true));

      await ECBindingService.bind(
        orgId,
        customerId,
        ecCustomerId,
        authcode,
        token,
        provider,
      );

      dispatch(setIsLoading(false));

      return { success: true };
    } catch (error) {
      dispatch(setIsLoading(false));

      // 優惠券已發完
      if (error.response?.data?.code === 'EcCouponInsufficientError') {
        dispatch(
          errorHandle(error, true, () => {
            const { lineAccountId } = parseJwt(token) || {};
            window.location.href = `https://line.me/R/ti/p/${lineAccountId}`;
          }),
        );
        return { isError: true };
      }

      dispatch(errorHandle(error, true));
      return { isError: true };
    }
  };

export const searchCustomers =
  (orgId, customerId, account, authcode, token, provider) => async dispatch => {
    try {
      dispatch(setIsLoading(true));

      const response = await ECBindingService.searchCustomers(
        orgId,
        customerId,
        account,
        authcode,
        token,
        provider,
      );

      dispatch(setIsLoading(false));

      return response;
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(errorHandle(error, true));
      return error?.response?.data?.code || false;
    }
  };

export const getIsBind =
  (orgId, customerId, token, provider) => async dispatch => {
    try {
      dispatch(setIsLoading(true));

      const response = await ECBindingService.checkIsBind(
        orgId,
        customerId,
        token,
        provider,
      );

      dispatch(setIsLoading(false));

      return !!response;
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(errorHandle(error, true));
      return false;
    }
  };

export const cancelBind =
  (orgId, customerId, token, provider) => async dispatch => {
    try {
      dispatch(setIsLoading(true));

      await ECBindingService.cancelBind(orgId, customerId, token, provider);

      dispatch(setIsLoading(false));
    } catch (error) {
      dispatch(setIsLoading(false));
      dispatch(errorHandle(error, true));
    }
  };

export const orderAddMessage = () => async (dispatch, getStore) => {
  const { data = {} } = getStore().ecBinding;
  const { actions = {} } = data;
  const { orderSearch = {} } = actions;
  const { messages = {} } = orderSearch;
  const { noOrder = [] } = messages;

  dispatch(
    setData({
      actions: {
        ...actions,
        orderSearch: {
          ...orderSearch,
          messages: {
            ...messages,
            noOrder: [
              ...(noOrder || []),
              {
                index: (noOrder || []).length,
                contentType: 'text/plain',
                data: {
                  content: '',
                },
              },
            ],
          },
        },
      },
    }),
  );
};

export const orderDeleteMessage = index => async (dispatch, getStore) => {
  const { data = {} } = getStore().ecBinding;
  const { actions = {} } = data;
  const { orderSearch = {} } = actions;
  const { messages = {} } = orderSearch;
  const { noOrder = [] } = messages;

  dispatch(
    setData({
      actions: {
        ...actions,
        orderSearch: {
          ...orderSearch,
          messages: {
            ...messages,
            noOrder: (noOrder || [])
              .filter((message, messageIndex) => messageIndex !== index)
              .map((message, messageIndex) => {
                return {
                  ...message,
                  index: messageIndex,
                };
              }),
          },
        },
      },
    }),
  );
};

export const orderUpMessage = index => async (dispatch, getStore) => {
  const { data = {} } = getStore().ecBinding;
  const { actions = {} } = data;
  const { orderSearch = {} } = actions;
  const { messages = {} } = orderSearch;
  const noOrder = [...(messages?.noOrder || [])];
  const tempMessage = noOrder[index - 1];
  noOrder[index - 1] = noOrder[index];
  noOrder[index] = tempMessage;

  dispatch(
    setData({
      actions: {
        ...actions,
        orderSearch: {
          ...orderSearch,
          messages: {
            ...messages,
            noOrder: noOrder.map((message, messageIndex) => {
              return {
                ...message,
                index: messageIndex,
              };
            }),
          },
        },
      },
    }),
  );
};

export const orderDownMessage = index => async (dispatch, getStore) => {
  const { data = {} } = getStore().ecBinding;
  const { actions = {} } = data;
  const { orderSearch = {} } = actions;
  const { messages = {} } = orderSearch;
  const noOrder = [...(messages?.noOrder || [])];
  const tempMessage = noOrder[index + 1];
  noOrder[index + 1] = noOrder[index];
  noOrder[index] = tempMessage;

  dispatch(
    setData({
      actions: {
        ...actions,
        orderSearch: {
          ...orderSearch,
          messages: {
            ...messages,
            noOrder: noOrder.map((message, messageIndex) => {
              return {
                ...message,
                index: messageIndex,
              };
            }),
          },
        },
      },
    }),
  );
};

export const createLineLiff = (orgId, liffName) => async dispatch => {
  try {
    await ECBindingService.createLineLiff(orgId, liffName);

    dispatch(
      enqueueSnackbar({
        variant: SnackBarType.SUCCESS,
        message: i18n.t('autoGenerateSuccess'),
      }),
    );
  } catch (error) {
    if (error?.response?.data.code === 'error/maximum_liff_apps') {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: i18n.t('error/maximum_liff_apps'),
        }),
      );
    } else {
      dispatch(
        enqueueSnackbar({
          variant: SnackBarType.ERROR,
          message: error?.response?.data?.error || error?.message,
        }),
      );
    }
  }
};

export const deleteLineLiff = (orgId, liffName) => async dispatch => {
  try {
    await ECBindingService.deleteLineLiff(orgId, liffName);

    dispatch(
      enqueueSnackbar({
        variant: SnackBarType.SUCCESS,
        message: i18n.t('deleteLiffSuccess'),
      }),
    );
  } catch (error) {
    dispatch(
      enqueueSnackbar({
        variant: SnackBarType.ERROR,
        message: error?.response?.data?.error || error?.message,
      }),
    );
  }
};

export default ecBindingSlice.reducer;
